/**
 * WalletKyc — R1 rewrite. Preserves backend contract 1:1:
 *   GET  /kyc/status
 *   POST /kyc/submit  multipart(doc_type, id_front, [id_back], selfie)
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Check, X, Clock, Camera } from "lucide-react";
import { Link } from "react-router-dom";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui-r1/Button";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";

const DOC_TYPES = [
  { key: "passport", label: "Passport" },
  { key: "drivers_license", label: "Driver's license" },
  { key: "national_id", label: "National ID" },
];

const MAX_MB = 10;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

export default function WalletKyc() {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [docType, setDocType] = useState("passport");
  const [files, setFiles] = useState({ id_front: null, id_back: null, selfie: null });
  const [previews, setPreviews] = useState({ id_front: null, id_back: null, selfie: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/kyc/status").then(({ data }) => setStatus(data)).catch((e) => setError(extractError(e)));
  }, []);

  const setFile = (kind, f) => {
    if (!f) {
      setFiles((p) => ({ ...p, [kind]: null }));
      setPreviews((p) => ({ ...p, [kind]: null }));
      return;
    }
    if (!ALLOWED.includes(f.type)) { toast.error(`Unsupported type: ${f.type}`); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Max size ${MAX_MB} MB`); return; }
    setFiles((p) => ({ ...p, [kind]: f }));
    if (f.type.startsWith("image/")) {
      setPreviews((p) => ({ ...p, [kind]: URL.createObjectURL(f) }));
    } else {
      setPreviews((p) => ({ ...p, [kind]: null }));
    }
  };

  const submit = async () => {
    if (!files.id_front || !files.selfie) {
      setError("ID front and selfie are required");
      return;
    }
    setError(null); setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("id_front", files.id_front);
    fd.append("selfie", files.selfie);
    if (files.id_back) fd.append("id_back", files.id_back);
    try {
      await api.post("/kyc/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documents uploaded · pending review");
      const { data } = await api.get("/kyc/status");
      setStatus(data);
      await refresh();
      setFiles({ id_front: null, id_back: null, selfie: null });
      setPreviews({ id_front: null, id_back: null, selfie: null });
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const s = status?.kyc_status || "none";

  return (
    <div data-testid="wallet-kyc">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-2">Identity verification</div>
        <h2 className="font-display italic text-3xl text-ink">KYC · Unlock withdrawals</h2>
        <p className="text-sm text-ink-2 mt-2 max-w-xl">
          Required before your first withdrawal and for deposits above ${status?.kyc_threshold_usd?.toFixed(0) ?? 200}.
          Documents are encrypted at rest and only reviewed by audit-logged admins.
        </p>
      </div>

      <StatusBanner status={s} reason={status?.reject_reason} />

      {(s === "none" || s === "rejected") && (
        <div className="mt-6 max-w-2xl" data-testid="kyc-form">
          <Card className="mb-4">
            <CardBody>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-2">1 · Document type</div>
              <div className="grid grid-cols-3 gap-2">
                {DOC_TYPES.map((d) => (
                  <button
                    key={d.key}
                    onClick={() => setDocType(d.key)}
                    className={
                      "py-2.5 text-xs border rounded-lg transition " +
                      (docType === d.key
                        ? "border-pink text-pink bg-pink/10"
                        : "border-line text-ink-2 hover:text-ink hover:border-ink-2")
                    }
                    data-testid={`doctype-${d.key}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card className="mb-4">
            <CardBody>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-3">2 · Upload documents</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Dropzone kind="id_front" label="ID · front" file={files.id_front} preview={previews.id_front} onSet={setFile} required />
                {docType !== "passport" && (
                  <Dropzone kind="id_back" label="ID · back" file={files.id_back} preview={previews.id_back} onSet={setFile} />
                )}
                <Dropzone kind="selfie" label="Selfie holding ID" icon={<Camera className="w-3.5 h-3.5" />} file={files.selfie} preview={previews.selfie} onSet={setFile} required />
              </div>
            </CardBody>
          </Card>

          {error && (
            <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2 mb-4" data-testid="kyc-error">
              {error}
            </div>
          )}

          <Button
            onClick={submit}
            disabled={busy}
            loading={busy}
            variant="primary"
            size="lg"
            className="w-full sm:w-auto"
            data-testid="kyc-submit-btn"
          >
            {busy ? "Uploading…" : "Submit for review"}
          </Button>
        </div>
      )}

      <div className="mt-8">
        <Link to="/wallet/withdraw" className="text-[10px] tracking-[0.3em] uppercase text-ink-2 hover:text-pink" data-testid="kyc-link-back-withdraw">
          Back to withdraw →
        </Link>
      </div>
    </div>
  );
}

function StatusBanner({ status, reason }) {
  const cfg = {
    none: {
      icon: <Upload className="w-4 h-4" />,
      variant: "muted",
      title: "Not started",
      body: "Upload your ID and a selfie to begin verification.",
    },
    pending: {
      icon: <Clock className="w-4 h-4 text-gold" />,
      variant: "gold",
      title: "Under review",
      body: "Your documents were received. Reviews typically complete within 24 hours.",
    },
    approved: {
      icon: <Check className="w-4 h-4 text-success" />,
      variant: "success",
      title: "Approved",
      body: "Your identity has been verified. All account features are unlocked.",
    },
    rejected: {
      icon: <X className="w-4 h-4 text-danger" />,
      variant: "danger",
      title: "Rejected",
      body: reason || "Please review the notes and resubmit.",
    },
  };
  const m = cfg[status] || cfg.none;
  return (
    <Card data-testid={`kyc-status-${status}`}>
      <CardBody className="flex items-start gap-4">
        <div className="shrink-0">{m.icon}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={m.variant}>{m.title}</Badge>
          </div>
          <div className="text-sm text-ink">{m.body}</div>
        </div>
      </CardBody>
    </Card>
  );
}

function Dropzone({ kind, label, file, preview, onSet, icon, required }) {
  return (
    <label className="block cursor-pointer group" data-testid={`dropzone-${kind}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-1.5 flex items-center gap-1.5">
        {icon || <Upload className="w-3 h-3" />} {label} {required && <span className="text-danger">*</span>}
      </div>
      <div className="aspect-video border-2 border-dashed border-line rounded-lg flex items-center justify-center overflow-hidden bg-base group-hover:border-pink group-hover:bg-elevated transition">
        {preview ? (
          <img src={preview} alt={label} className="w-full h-full object-contain" />
        ) : file ? (
          <div className="text-[10px] font-mono text-ink-2 px-2 text-center">{file.name}</div>
        ) : (
          <div className="text-[10px] tracking-widest uppercase text-ink-3 flex items-center gap-1.5">
            <Upload className="w-3 h-3" /> Click to upload
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => onSet(kind, e.target.files?.[0] || null)}
        data-testid={`file-${kind}`}
      />
    </label>
  );
}
